import React, { useEffect, useRef, useState, useCallback } from 'react';
import './PdfViewer.scss';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import type { PDFDocumentProxy, PageViewport } from 'pdfjs-dist';
import type { PageRedactions, RedactionCoords } from '../../types/resource';
import { buildSnippetSearch, resolveSnippetMatches } from './snippetSearch';
import { api } from '../../services/api';
import { showApiErrorToast } from '../../services/errorToast';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const SCALE_STORAGE_KEY = 'iio-pdf-zoom';
// The dropdown choice (preset, page-width/fit, custom), so it survives with the scale.
const SCALE_MODE_STORAGE_KEY = 'iio-pdf-zoom-mode';
const DEFAULT_SCALE = 1.5;
// Angular's vendor pdfjs builds viewports at 96 DPI; stored coords are pdf_pts × CSS_UNITS.
const CSS_UNITS = 96 / 72;
const PRESET_SCALES = ['0.5', '0.75', '1', '1.25', '1.5', '2', '3', '4'];
const THUMB_SCALE = 0.15;
// Ceiling on canvas backing-store pixels; above this browsers refuse to allocate.
const MAX_CANVAS_PIXELS = 16_777_216;

interface PdfViewerProps {
  pdfData: ArrayBuffer;
  filename?: string;
  redactions?: PageRedactions[];
  onRedactionsChange?: (redactions: PageRedactions[]) => void;
  /** snippet may contain <em> markers around the matched term */
  searchRequest?: { snippet: string; seq: number } | null;
  /** Document the viewer is rendered into — the pop-out window's when detached. */
  targetDocument?: Document;
  /**
   * File handed to print/download when it differs from the rendered one — the
   * server-burned redacted PDF. Redactions are drawn as overlays over the original, so
   * without this the user would print an unredacted document.
   */
  sourceUrl?: string;
  /** MANAGE_DOWNLOADS: without it, print and download are unavailable. */
  canDownload?: boolean;
  /** Audit-trail callbacks, fired after the action is handed to the browser. */
  onPrinted?: () => void;
  onDownloaded?: () => void;
}

interface PageState {
  canvas: HTMLCanvasElement | null;
  svgOverlay: SVGSVGElement | null;
  textLayer: HTMLDivElement | null;
  viewport: PageViewport | null;
  wrapper: HTMLDivElement | null;
}

// Per-page text index built from the rendered text layer DOM
interface PageTextIndex {
  spans: HTMLSpanElement[];
  offsets: number[];   // start char index of each span in pageText
  spanTexts: string[]; // original text of each span (used when restoring after highlights)
  pageText: string;    // concatenated text
}

interface SearchMatch {
  pageNum: number;
  start: number; // char index in pageText
  end: number;
}

// Stored coords: Angular's redact.js stores offsetX / pageView.scale, where the viewport is
// built at pageView.scale × CSS_UNITS. This means stored = pdf_pts × CSS_UNITS. To convert:
//   load → svg = stored × (scale / CSS_UNITS) = pdf_pts × scale  ✓
//   save → stored = svg × (CSS_UNITS / scale)                      ✓
function storedCoordsToSvg(
  r: RedactionCoords,
  scale: number,
): { x: number; y: number; width: number; height: number } {
  const f = scale / CSS_UNITS;
  return { x: r.x * f, y: r.y * f, width: r.width * f, height: r.height * f };
}

function svgRectToStored(
  svgX: number, svgY: number, svgW: number, svgH: number,
  scale: number,
): RedactionCoords {
  const f = CSS_UNITS / scale;
  return { x: svgX * f, y: svgY * f, width: svgW * f, height: svgH * f };
}

// Build a char-offset index from the rendered text layer spans
function buildPageTextIndex(container: HTMLElement): PageTextIndex {
  // Only leaf text spans (role="presentation"); skip markedContent grouping spans
  const spans = Array.from(container.querySelectorAll<HTMLSpanElement>('span[role="presentation"]'));
  const spanTexts = spans.map(s => s.textContent ?? '');
  const offsets: number[] = [];
  let pos = 0;
  for (const t of spanTexts) { offsets.push(pos); pos += t.length; }
  return { spans, offsets, spanTexts, pageText: spanTexts.join('') };
}

// Apply highlights using Range API so browser-measured rects account for scaleX and all transforms.
// Never modifies span content — avoids inline-flow drift caused by injecting mark elements.
function applyPageHighlights(
  container: HTMLElement,
  index: PageTextIndex,
  matches: SearchMatch[],
  currentIdx: number,
  highlightAll: boolean,
  pageNum: number,
) {
  container.querySelectorAll('.pdf-highlight-box').forEach(el => el.remove());

  const toApply = matches
    .map((m, i) => ({ ...m, globalIdx: i }))
    .filter(m => m.pageNum === pageNum && (highlightAll || m.globalIdx === currentIdx));

  if (toApply.length === 0) return;

  const containerRect = container.getBoundingClientRect();

  // Collect all rects first (read phase — avoids interleaved read/write layout thrash)
  const pending: { isCurrent: boolean; left: number; top: number; width: number; height: number }[] = [];

  for (const m of toApply) {
    const isCurrent = m.globalIdx === currentIdx;
    for (let si = 0; si < index.spans.length; si++) {
      const spanStart = index.offsets[si];
      const spanEnd = spanStart + index.spanTexts[si].length;
      if (spanEnd <= m.start || spanStart >= m.end) continue;

      const charStart = Math.max(m.start, spanStart) - spanStart;
      const charEnd = Math.min(m.end, spanEnd) - spanStart;
      const textNode = index.spans[si].firstChild;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) continue;

      try {
        const range = document.createRange();
        range.setStart(textNode, charStart);
        range.setEnd(textNode, charEnd);
        for (const rect of Array.from(range.getClientRects())) {
          if (rect.width === 0 && rect.height === 0) continue;
          pending.push({
            isCurrent,
            left: rect.left - containerRect.left,
            top: rect.top - containerRect.top,
            width: rect.width,
            height: rect.height,
          });
        }
      } catch {
        // Range offsets out of bounds — skip
      }
    }
  }

  // Append all highlight divs (write phase)
  for (const { isCurrent, left, top, width, height } of pending) {
    const div = document.createElement('div');
    div.className = isCurrent ? 'pdf-highlight-box pdf-highlight-box--current' : 'pdf-highlight-box';
    div.style.cssText = `position:absolute;left:${left}px;top:${top}px;width:${width}px;height:${height}px`;
    container.appendChild(div);
  }
}

// Stable default: a fresh [] each render re-fires the redactions sync effect forever.
const NO_REDACTIONS: PageRedactions[] = [];

export default function PdfViewer({ pdfData, filename = 'document.pdf', redactions = NO_REDACTIONS,onRedactionsChange, searchRequest, targetDocument, sourceUrl, canDownload = true, onPrinted, onDownloaded }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [scaleSelectValue, setScaleSelectValue] = useState<string>(() => {
    const mode = localStorage.getItem(SCALE_MODE_STORAGE_KEY);
    if (mode) return mode;
    const saved = localStorage.getItem(SCALE_STORAGE_KEY);
    if (!saved) return String(DEFAULT_SCALE);
    return PRESET_SCALES.includes(saved) ? saved : 'custom';
  });
  const [scale, setScale] = useState<number>(() => {
    const saved = localStorage.getItem(SCALE_STORAGE_KEY);
    return saved ? parseFloat(saved) : DEFAULT_SCALE;
  });
  const [pendingRedactions, setPendingRedactions] = useState<PageRedactions[]>(redactions);
  const [redactMode, setRedactMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Guards print/download while the output file is being fetched.
  const [busy, setBusy] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [thumbnails, setThumbnails] = useState<Map<number, string>>(new Map());
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findMatches, setFindMatches] = useState<SearchMatch[]>([]);
  const [findCurrentIdx, setFindCurrentIdx] = useState(0);
  const [highlightAll, setHighlightAll] = useState(true);
  const [matchCase, setMatchCase] = useState(false);
  const pageStatesRef = useRef<Map<number, PageState>>(new Map());
  const pageTextIndexRef = useRef<Map<number, PageTextIndex>>(new Map());
  const redactModeRef = useRef(false);
  const pendingRedactionsRef = useRef(pendingRedactions);
  const programmaticScrollRef = useRef(false);
  const currentPageRef = useRef(1);
  // Refs so renderPage can apply current highlights without stale closures
  const findMatchesRef = useRef<SearchMatch[]>([]);
  const findCurrentIdxRef = useRef(0);
  const highlightAllRef = useRef(true);

  // 'external' = matches came from a snippet the user clicked in the review panel, so the
  // plain find-as-you-type effect must not recompute them from the find bar's query.
  const searchModeRef = useRef<'manual' | 'external'>('manual');
  const resolvedSeqRef = useRef(-1);
  // Bumped as each page's text index lands, so a snippet request that arrives before the
  // pages are indexed gets another chance to resolve.
  const [indexTick, setIndexTick] = useState(0);

  // Keep refs in sync
  useEffect(() => { redactModeRef.current = redactMode; }, [redactMode]);
  useEffect(() => { pendingRedactionsRef.current = pendingRedactions; }, [pendingRedactions]);
  useEffect(() => { findMatchesRef.current = findMatches; }, [findMatches]);
  useEffect(() => { findCurrentIdxRef.current = findCurrentIdx; }, [findCurrentIdx]);
  useEffect(() => { highlightAllRef.current = highlightAll; }, [highlightAll]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  useEffect(() => {
    let cancelled = false;
    // Clear any prior document up front so a resource without a usable PDF (e.g. a
    // processing exception returning an empty/invalid buffer) doesn't keep showing
    // the previously loaded document.
    setPdf(null);
    setNumPages(0);
    setLoadError(false);
    // ownerDocument tells pdf.js which document to register @font-face in. Without it
    // fonts land in the parent window's document and the detached window renders tofu —
    // the reason this used to run with disableFontFace, whose path-rendered glyphs have
    // no hinting and look jagged below ~200% zoom.
    const loadingTask = pdfjsLib.getDocument({
      data: pdfData.slice(0),
      ownerDocument: targetDocument ?? document,
    });
    loadingTask.promise.then((doc) => {
      if (!cancelled) { setPdf(doc); setNumPages(doc.numPages); }
    }).catch(() => {
      if (!cancelled) { setPdf(null); setNumPages(0); setLoadError(true); }
    });
    return () => { cancelled = true; loadingTask.destroy(); };
    // targetDocument: detaching/popping in moves the canvases to another document, so the
    // document must be reloaded to register its fonts there.
  }, [pdfData, targetDocument]);

  // Report unsaved redactions so the viewer can warn before Next/Previous/Close.
  const setPendingChange = useResourceViewerStore((s) => s.setPendingChange);
  useEffect(() => {
    setPendingChange('redaction', hasUnsavedChanges);
  }, [hasUnsavedChanges, setPendingChange]);
  useEffect(() => () => setPendingChange('redaction', false), [setPendingChange]);

  useEffect(() => {
    setPendingRedactions(Array.isArray(redactions) ? redactions : []);
    setHasUnsavedChanges(false);
  }, [redactions]);

  useEffect(() => {
    localStorage.setItem(SCALE_STORAGE_KEY, String(scale));
  }, [scale]);

  useEffect(() => {
    localStorage.setItem(SCALE_MODE_STORAGE_KEY, scaleSelectValue);
  }, [scaleSelectValue]);

  // Track current page by scroll position
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      for (const [pageNum, state] of pageStatesRef.current) {
        if (!state.wrapper) continue;
        const rect = state.wrapper.getBoundingClientRect();
        const containerRect = el.getBoundingClientRect();
        if (rect.top <= containerRect.top + containerRect.height / 2 && rect.bottom >= containerRect.top) {
          setCurrentPage(pageNum);
          setPageInputValue(String(pageNum));
          break;
        }
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToPage = useCallback((pageNum: number) => {
    const state = pageStatesRef.current.get(pageNum);
    if (state?.wrapper && scrollContainerRef.current) {
      programmaticScrollRef.current = true;
      const containerTop = scrollContainerRef.current.getBoundingClientRect().top;
      const wrapperTop = state.wrapper.getBoundingClientRect().top;
      scrollContainerRef.current.scrollTop += wrapperTop - containerTop - 14;
      requestAnimationFrame(() => { programmaticScrollRef.current = false; });
    }
  }, []);

  function goToPage(pageNum: number) {
    const clamped = Math.max(1, Math.min(numPages, pageNum));
    scrollToPage(clamped);
    setCurrentPage(clamped);
    setPageInputValue(String(clamped));
  }

  // Scale that fits a page of pdfW × pdfH (unscaled) to the scroll area.
  function applyFit(mode: string, pdfW: number, pdfH: number) {
    const scroll = scrollContainerRef.current;
    if (!scroll?.clientWidth) return;
    const byWidth = (scroll.clientWidth - 28) / pdfW;
    setScale(mode === 'page-width' ? byWidth : Math.min(byWidth, (scroll.clientHeight - 28) / pdfH));
  }

  // Page Width / Page Fit re-fit each document to its own first page.
  useEffect(() => {
    if (!pdf || (scaleSelectValue !== 'page-width' && scaleSelectValue !== 'page-fit')) return;
    let cancelled = false;
    pdf.getPage(1).then((page) => {
      const vp = page.getViewport({ scale: 1 });
      if (!cancelled) applyFit(scaleSelectValue, vp.width, vp.height);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pdf]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleScaleSelect(value: string) {
    setScaleSelectValue(value);
    if (value === 'page-width' || value === 'page-fit') {
      const firstState = pageStatesRef.current.get(1);
      if (firstState?.viewport) {
        applyFit(value, firstState.viewport.width / scale, firstState.viewport.height / scale);
      }
    } else {
      setScale(parseFloat(value));
    }
  }

  function handleFullscreen() {
    viewerRef.current?.requestFullscreen().catch(() => {});
  }

  // Respond to external search requests (a snippet clicked in the review panel).
  // The snippet's whitespace won't match the PDF text layer, so snippetSearch anchors on a
  // short window and highlights the <em> term near it — not the window itself.
  useEffect(() => {
    if (!searchRequest?.snippet.trim()) return;
    if (resolvedSeqRef.current === searchRequest.seq) return;

    const search = buildSnippetSearch(searchRequest.snippet);
    if (search.candidates.length === 0) return;

    const pages = Array.from(pageTextIndexRef.current.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([pageNum, index]) => ({ pageNum, text: index.pageText }));
    const { query, matches } = resolveSnippetMatches(search, pages);
    if (matches.length > 0) resolvedSeqRef.current = searchRequest.seq;

    searchModeRef.current = 'external';
    setFindOpen(true);
    setFindQuery(query);
    setFindMatches(matches);
    setFindCurrentIdx(0);
  }, [searchRequest, indexTick]);

  // Recompute matches when the user drives the find bar directly.
  useEffect(() => {
    if (searchModeRef.current === 'external') return;
    if (!findOpen || !findQuery.trim()) {
      setFindMatches([]);
      setFindCurrentIdx(0);
      return;
    }
    const query = matchCase ? findQuery : findQuery.toLowerCase();
    const matches: SearchMatch[] = [];
    for (const [pageNum, index] of Array.from(pageTextIndexRef.current.entries()).sort((a, b) => a[0] - b[0])) {
      const text = matchCase ? index.pageText : index.pageText.toLowerCase();
      let pos = text.indexOf(query);
      while (pos !== -1) {
        matches.push({ pageNum, start: pos, end: pos + query.length });
        pos = text.indexOf(query, pos + 1);
      }
    }
    setFindMatches(matches);
    setFindCurrentIdx(0);
  }, [findQuery, findOpen, matchCase]);

  function findNav(direction: 1 | -1) {
    if (findMatches.length === 0) return;
    const next = (findCurrentIdx + direction + findMatches.length) % findMatches.length;
    setFindCurrentIdx(next);
  }

  function closeFind() {
    searchModeRef.current = 'manual';
    setFindOpen(false);
    setFindQuery('');
  }

  // Re-apply highlights; scroll active match into view within the scroll container if off-screen
  useEffect(() => {
    for (const [pageNum, index] of pageTextIndexRef.current) {
      const textLayer = pageStatesRef.current.get(pageNum)?.textLayer;
      if (textLayer) applyPageHighlights(textLayer, index, findMatches, findCurrentIdx, highlightAll, pageNum);
    }

    if (findMatches.length > 0 && scrollContainerRef.current) {
      const match = findMatches[findCurrentIdx];
      const textLayer = pageStatesRef.current.get(match.pageNum)?.textLayer;
      const box = textLayer?.querySelector<HTMLElement>('.pdf-highlight-box--current');
      if (box) {
        const MARGIN = 24;
        const boxRect = box.getBoundingClientRect();
        const sc = scrollContainerRef.current;
        const cRect = sc.getBoundingClientRect();
        if (boxRect.top < cRect.top + MARGIN) {
          sc.scrollTop -= cRect.top + MARGIN - boxRect.top;
        } else if (boxRect.bottom > cRect.bottom - MARGIN) {
          sc.scrollTop += boxRect.bottom - cRect.bottom + MARGIN;
        }
      }
    }
  }, [findMatches, findCurrentIdx, highlightAll]);

  const renderRedactions = useCallback(
    (pageNum: number, svg: SVGSVGElement, viewport: PageViewport, currentRedactions: PageRedactions[]) => {
      Array.from(svg.querySelectorAll('rect[data-saved]')).forEach((el) => el.remove());

      const pageRedacts = currentRedactions.find((p) => p.pageNumber === pageNum);
      if (!pageRedacts) return;

      for (const r of pageRedacts.redactions) {
        const rect = storedCoordsToSvg(r, viewport.scale);
        const el = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        el.setAttribute('data-saved', '1');
        el.setAttribute('x', String(rect.x));
        el.setAttribute('y', String(rect.y));
        el.setAttribute('width', String(rect.width));
        el.setAttribute('height', String(rect.height));
        el.setAttribute('fill', 'rgba(0,0,0,0.5)');
        el.style.cursor = 'pointer';
        el.addEventListener('click', (e) => {
          if (!redactModeRef.current) return;
          e.stopPropagation();
          if (!window.confirm('Do you want to remove the current redaction?')) return;
          setPendingRedactions((prev) => {
            const next = prev.map((p) => {
              if (p.pageNumber !== pageNum) return p;
              return { ...p, redactions: p.redactions.filter((rd) => rd !== r) };
            });
            setHasUnsavedChanges(true);
            return next;
          });
        });
        svg.appendChild(el);
      }
    },
    [],
  );

  useEffect(() => {
    for (const [pageNum, state] of pageStatesRef.current) {
      if (state.svgOverlay) {
        state.svgOverlay.style.pointerEvents = redactMode ? 'auto' : 'none';
        state.svgOverlay.style.cursor = redactMode ? 'crosshair' : 'default';
      }
      // Text layer sits above the SVG. Let it capture events for text selection when
      // not redacting; disable so redaction drags reach the SVG underneath.
      if (state.textLayer) {
        state.textLayer.style.pointerEvents = redactMode ? 'none' : 'auto';
      }
      if (state.svgOverlay && state.viewport) {
        renderRedactions(pageNum, state.svgOverlay, state.viewport, pendingRedactionsRef.current);
      }
    }
  }, [redactMode, renderRedactions]);

  useEffect(() => {
    for (const [pageNum, state] of pageStatesRef.current) {
      if (state.svgOverlay && state.viewport) {
        renderRedactions(pageNum, state.svgOverlay, state.viewport, pendingRedactions);
      }
    }
  }, [pendingRedactions, renderRedactions]);

  const renderPage = useCallback(
    async (pageNum: number, container: HTMLDivElement, pdfDoc: PDFDocumentProxy, isCancelled: () => boolean) => {
      const page = await pdfDoc.getPage(pageNum);
      if (isCancelled()) { page.cleanup(); return; }
      const viewport = page.getViewport({ scale });

      // Canvas
      let canvas = container.querySelector<HTMLCanvasElement>(`canvas[data-page="${pageNum}"]`);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.dataset.page = String(pageNum);
        container.appendChild(canvas);
      }
      // Backing store at device resolution, CSS box at viewport size. Without this the
      // bitmap is upscaled by the browser on HiDPI / Windows display scaling and glyphs
      // (painted as paths, see disableFontFace) come out jagged.
      const win = container.ownerDocument.defaultView ?? window;
      let outputScale = win.devicePixelRatio || 1;
      const maxScale = Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height));
      if (outputScale > maxScale) outputScale = Math.max(1, maxScale);
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.style.display = 'block';
      await page.render({
        canvasContext: canvas.getContext('2d')!,
        viewport,
        transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
      }).promise;
      if (isCancelled()) { page.cleanup(); return; }

      // SVG redaction overlay
      let svg = container.querySelector<SVGSVGElement>(`svg[data-page="${pageNum}"]`);
      if (!svg) {
        svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.dataset.page = String(pageNum);
        svg.style.cssText = 'position:absolute;top:0;left:0';
        container.appendChild(svg);

        let drawStart: { x: number; y: number } | null = null;
        let previewRect: SVGRectElement | null = null;

        svg.addEventListener('mousedown', (e) => {
          if (!redactModeRef.current) return;
          const bounds = svg!.getBoundingClientRect();
          drawStart = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
          previewRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          previewRect.setAttribute('fill', 'rgba(0,0,0,0.4)');
          previewRect.setAttribute('stroke', '#ff0000');
          previewRect.setAttribute('stroke-width', '1');
          svg!.appendChild(previewRect);
          e.preventDefault();
        });

        svg.addEventListener('mousemove', (e) => {
          if (!drawStart || !previewRect) return;
          const bounds = svg!.getBoundingClientRect();
          const curX = e.clientX - bounds.left;
          const curY = e.clientY - bounds.top;
          previewRect.setAttribute('x', String(Math.min(drawStart.x, curX)));
          previewRect.setAttribute('y', String(Math.min(drawStart.y, curY)));
          previewRect.setAttribute('width', String(Math.abs(curX - drawStart.x)));
          previewRect.setAttribute('height', String(Math.abs(curY - drawStart.y)));
        });

        const commitDraw = (e: MouseEvent) => {
          if (!drawStart || !previewRect || !redactModeRef.current) {
            drawStart = null; previewRect?.remove(); previewRect = null; return;
          }
          const bounds = svg!.getBoundingClientRect();
          const curX = e.clientX - bounds.left;
          const curY = e.clientY - bounds.top;
          const svgX = Math.min(drawStart.x, curX);
          const svgY = Math.min(drawStart.y, curY);
          const svgW = Math.abs(curX - drawStart.x);
          const svgH = Math.abs(curY - drawStart.y);
          previewRect.remove(); previewRect = null; drawStart = null;
          if (svgW < 3 || svgH < 3) return;
          const state = pageStatesRef.current.get(pageNum);
          if (!state?.viewport) return;
          const coords = svgRectToStored(svgX, svgY, svgW, svgH, state.viewport.scale);
          setPendingRedactions((prev) => {
            const existing = prev.find((p) => p.pageNumber === pageNum);
            const next = existing
              ? prev.map((p) => p.pageNumber === pageNum ? { ...p, redactions: [...p.redactions, coords] } : p)
              : [...prev, { pageNumber: pageNum, redactions: [coords] }];
            setHasUnsavedChanges(true);
            return next;
          });
        };

        svg.addEventListener('mouseup', commitDraw);
        svg.addEventListener('mouseleave', (e) => { if (drawStart) commitDraw(e as MouseEvent); });
      }

      svg.setAttribute('width', String(viewport.width));
      svg.setAttribute('height', String(viewport.height));
      svg.style.pointerEvents = redactModeRef.current ? 'auto' : 'none';
      svg.style.cursor = redactModeRef.current ? 'crosshair' : 'default';

      // Text layer — positioned on top of canvas, transparent text for accurate search hit location
      let textLayerDiv = container.querySelector<HTMLDivElement>('.pdf-text-layer');
      if (!textLayerDiv) {
        textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'pdf-text-layer';
        container.appendChild(textLayerDiv);
      }
      textLayerDiv.innerHTML = '';
      // Selectable unless this page renders while redaction mode is active (kept in
      // sync afterwards by the redactMode effect).
      textLayerDiv.style.pointerEvents = redactModeRef.current ? 'none' : 'auto';
      // TextLayer constructor calls setLayerDimensions which sets width/height as
      // calc(var(--total-scale-factor) * pageWidthPts px). Set the var so it resolves correctly.
      textLayerDiv.style.setProperty('--total-scale-factor', String(viewport.scale));

      const textContent = await page.getTextContent();
      if (isCancelled()) { page.cleanup(); return; }
      const tl = new (pdfjsLib as any).TextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport,
      });
      await tl.render();
      if (isCancelled()) { page.cleanup(); return; }

      const textIndex = buildPageTextIndex(textLayerDiv);
      pageTextIndexRef.current.set(pageNum, textIndex);
      setIndexTick(t => t + 1);
      applyPageHighlights(textLayerDiv, textIndex, findMatchesRef.current, findCurrentIdxRef.current, highlightAllRef.current, pageNum);

      pageStatesRef.current.set(pageNum, { canvas, svgOverlay: svg, textLayer: textLayerDiv, viewport, wrapper: container });
      renderRedactions(pageNum, svg, viewport, pendingRedactionsRef.current);

      // Thumbnail for sidebar
      const thumbVp = page.getViewport({ scale: THUMB_SCALE });
      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = Math.round(thumbVp.width);
      thumbCanvas.height = Math.round(thumbVp.height);
      const thumbCtx = thumbCanvas.getContext('2d');
      if (thumbCtx) {
        await page.render({ canvasContext: thumbCtx, viewport: thumbVp }).promise;
        if (!isCancelled()) {
          const dataUrl = thumbCanvas.toDataURL('image/jpeg', 0.7);
          setThumbnails(prev => { const n = new Map(prev); n.set(pageNum, dataUrl); return n; });
        }
      }

      page.cleanup();
    },
    [scale, renderRedactions],
  );

  useEffect(() => {
    if (!pdf || !containerRef.current) return;
    let cancelled = false;
    const isCancelled = () => cancelled;

    const container = containerRef.current;
    container.innerHTML = '';
    pageStatesRef.current.clear();
    pageTextIndexRef.current.clear();
    setThumbnails(new Map());

    // Re-rendering rebuilds every page element, so scroll position is lost; restore it
    // (zoom changes, and reloads when the viewer moves to the detached window).
    const restorePage = currentPageRef.current;

    const renderAll = async () => {
      for (let i = 1; i <= numPages; i++) {
        if (cancelled) break;
        const pageWrapper = document.createElement('div');
        pageWrapper.style.cssText = [
          'position:relative',
          'display:block',
          'width:fit-content',
          'margin:14px auto',
          'background:#fff',
          'box-shadow:0 0 10px rgba(0,0,0,0.5)',
        ].join(';');
        container.appendChild(pageWrapper);
        await renderPage(i, pageWrapper, pdf, isCancelled);
        if (i === restorePage && restorePage > 1) scrollToPage(restorePage);
      }
    };
    renderAll();

    return () => { cancelled = true; };
  }, [pdf, scale, numPages, renderPage, scrollToPage]);

  // The bytes to hand out: the redacted file when the server has burned one, else what is
  // on screen. Fetched per action so a redaction finished since load is picked up.
  async function outputBytes(): Promise<ArrayBuffer> {
    if (!sourceUrl) return pdfData;
    return api.getArrayBuffer(sourceUrl);
  }

  // Print through a hidden iframe so the browser's PDF print dialog opens over the viewer,
  // rather than handing the document off to a new tab.
  async function handlePrint() {
    if (!canDownload || busy) return;
    setBusy(true);
    let data: ArrayBuffer;
    try {
      data = await outputBytes();
    } catch (e) {
      showApiErrorToast(e);
      setBusy(false);
      return;
    }
    setBusy(false);
    const doc = targetDocument ?? document;
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const iframe = doc.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:1px;height:1px;left:-1px;top:-1px;visibility:hidden';
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
      iframe.remove();
    };
    iframe.onload = () => {
      // The embedded PDF needs a moment past load before it will accept print().
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          cleanup();
          return;
        }
        // The dialog is modal and blocks this frame, so drop the iframe once it closes;
        // the fallback covers browsers that never fire afterprint.
        iframe.contentWindow?.addEventListener('afterprint', cleanup, { once: true });
        setTimeout(cleanup, 60_000);
      }, 200);
    };
    iframe.src = url;
    doc.body.appendChild(iframe);
    onPrinted?.();
  }

  async function handleDownload() {
    if (!canDownload || busy) return;
    setBusy(true);
    let data: ArrayBuffer;
    try {
      data = await outputBytes();
    } catch (e) {
      showApiErrorToast(e);
      setBusy(false);
      return;
    }
    setBusy(false);
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onDownloaded?.();
  }

  function handleSave() {
    onRedactionsChange?.(pendingRedactions);
    setHasUnsavedChanges(false);
  }

  return (
    <div className="pdf-viewer" ref={viewerRef} data-testid="viewer-pdf-panel">
      <div className="pdf-toolbar">
        <div className="pdf-toolbar-left">
          <button
            className={`pdf-tb-btn${sidebarOpen ? ' active' : ''}`}
            title="Toggle Sidebar"
            data-testid="viewer-pdf-sidebar-btn"
            onClick={() => setSidebarOpen(v => !v)}
          >
            <i className="fas fa-bars" />
          </button>
          <button
            className={`pdf-tb-btn${findOpen ? ' active' : ''}`}
            title="Find in Document"
            data-testid="viewer-pdf-find-btn"
            onClick={() => { searchModeRef.current = 'manual'; setFindOpen(v => !v); }}
          >
            <i className="fas fa-search" />
          </button>
          <div className="pdf-tb-sep" />
          <div className="pdf-tb-split">
            <button
              className="pdf-tb-btn"
              title="Previous Page"
              data-testid="viewer-pdf-prev-page-btn"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              <i className="fas fa-chevron-up" />
            </button>
            <div className="pdf-tb-sep" />
            <button
              className="pdf-tb-btn"
              title="Next Page"
              data-testid="viewer-pdf-next-page-btn"
              disabled={currentPage >= numPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              <i className="fas fa-chevron-down" />
            </button>
          </div>
          <input
            className="pdf-tb-page-input"
            data-testid="viewer-pdf-page-input"
            type="number"
            min={1}
            max={numPages || 1}
            value={pageInputValue}
            onChange={(e) => setPageInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') goToPage(parseInt(pageInputValue, 10) || currentPage); }}
            onBlur={() => {
              const n = parseInt(pageInputValue, 10);
              if (!isNaN(n)) goToPage(n); else setPageInputValue(String(currentPage));
            }}
          />
          <span className="pdf-tb-label">of {numPages}</span>
        </div>

        <div className="pdf-toolbar-center">
          <div className="pdf-tb-split">
            <button
              className="pdf-tb-btn"
              title="Zoom Out"
              data-testid="viewer-pdf-zoom-out-btn"
              onClick={() => {
                const next = parseFloat(Math.max(0.5, scale - 0.25).toFixed(2));
                setScale(next);
                setScaleSelectValue(PRESET_SCALES.includes(String(next)) ? String(next) : 'custom');
              }}
            >
              <i className="fas fa-minus" />
            </button>
            <div className="pdf-tb-sep" />
            <button
              className="pdf-tb-btn"
              title="Zoom In"
              data-testid="viewer-pdf-zoom-in-btn"
              onClick={() => {
                const next = parseFloat(Math.min(4, scale + 0.25).toFixed(2));
                setScale(next);
                setScaleSelectValue(PRESET_SCALES.includes(String(next)) ? String(next) : 'custom');
              }}
            >
              <i className="fas fa-plus" />
            </button>
          </div>
          <select
            className="pdf-tb-scale-select"
            data-testid="viewer-pdf-scale-select"
            value={scaleSelectValue}
            onChange={(e) => handleScaleSelect(e.target.value)}
          >
            <option value="page-width">Page Width</option>
            <option value="page-fit">Page Fit</option>
            <option value="0.5">50%</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
            <option value="2">200%</option>
            <option value="3">300%</option>
            <option value="4">400%</option>
            {!PRESET_SCALES.includes(scaleSelectValue) && scaleSelectValue !== 'page-width' && scaleSelectValue !== 'page-fit' && (
              <option value="custom">{Math.round(scale * 100)}%</option>
            )}
          </select>
        </div>

        <div className="pdf-toolbar-right">
          <button className="pdf-tb-btn" title="Full Screen" onClick={handleFullscreen} data-testid="viewer-pdf-fullscreen-btn">
            <i className="fas fa-expand-arrows-alt" />
          </button>
          <button
            className="pdf-tb-btn"
            title={canDownload ? 'Print' : 'Printing requires the Manage Downloads permission'}
            data-testid="viewer-print-btn"
            disabled={!canDownload || busy}
            onClick={() => void handlePrint()}
          >
            <i className="fas fa-print" />
          </button>
          <button
            className="pdf-tb-btn"
            title={canDownload ? 'Download' : 'Downloading requires the Manage Downloads permission'}
            data-testid="viewer-download-btn"
            disabled={!canDownload || busy}
            onClick={() => void handleDownload()}
          >
            <i className="fas fa-download" />
          </button>
          {onRedactionsChange && (
            <>
              <button
                className={`pdf-tb-btn${redactMode ? ' active' : ''}`}
                title={redactMode ? 'Exit redaction mode' : 'Redact'}
                data-testid="viewer-redact-btn"
                onClick={() => setRedactMode((v) => !v)}
              >
                <i className="fas fa-eraser" />
              </button>
              <button
                className="pdf-tb-btn"
                title="Save redactions"
                data-testid="viewer-redactions-save-btn"
                disabled={!hasUnsavedChanges}
                onClick={handleSave}
              >
                <i className="fas fa-save" />
              </button>
            </>
          )}
        </div>
      </div>

      {findOpen && (
        <div className="pdf-findbar" data-testid="viewer-pdf-find-panel">
          <input
            className="pdf-find-input"
            data-testid="viewer-pdf-find-input"
            type="text"
            placeholder="Find in document…"
            value={findQuery}
            autoFocus
            onChange={e => {
              searchModeRef.current = 'manual';
              setFindQuery(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') findNav(e.shiftKey ? -1 : 1);
              if (e.key === 'Escape') closeFind();
            }}
          />
          <span className="pdf-tb-label" style={{ minWidth: 72 }}>
            {findQuery.trim()
              ? findMatches.length > 0 ? `${findCurrentIdx + 1} of ${findMatches.length}` : 'No results'
              : ''}
          </span>
          <button className="pdf-tb-btn" title="Previous match" data-testid="viewer-pdf-find-prev-btn" disabled={findMatches.length === 0} onClick={() => findNav(-1)}>
            <i className="fas fa-chevron-up" />
          </button>
          <button className="pdf-tb-btn" title="Next match" data-testid="viewer-pdf-find-next-btn" disabled={findMatches.length === 0} onClick={() => findNav(1)}>
            <i className="fas fa-chevron-down" />
          </button>
          <label className="pdf-findbar-check">
            <input type="checkbox" data-testid="viewer-pdf-highlight-all-checkbox" checked={highlightAll} onChange={e => setHighlightAll(e.target.checked)} />
            Highlight all
          </label>
          <label className="pdf-findbar-check">
            <input
              type="checkbox"
              checked={matchCase}
              data-testid="viewer-pdf-match-case-checkbox"
              onChange={e => { searchModeRef.current = 'manual'; setMatchCase(e.target.checked); }}
            />
            Match case
          </label>
          <button className="pdf-tb-btn" title="Close" onClick={closeFind} data-testid="viewer-pdf-find-close-btn">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      <div className="pdf-body">
        {sidebarOpen && (
          <div className="pdf-sidebar" data-testid="viewer-pdf-sidebar-panel">
            {Array.from({ length: numPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                data-testid="viewer-pdf-thumbnail-item"
                className={`pdf-sidebar-page${currentPage === n ? ' active' : ''}`}
                onClick={() => goToPage(n)}
              >
                {thumbnails.has(n)
                  ? <img src={thumbnails.get(n)} alt={`Page ${n}`} className="pdf-sidebar-thumb" />
                  : <div className="pdf-sidebar-thumb pdf-sidebar-thumb--loading"><i className="fas fa-file" /></div>
                }
                <span className="pdf-sidebar-num">{n}</span>
              </button>
            ))}
          </div>
        )}
        <div ref={scrollContainerRef} className="pdf-scroll" data-testid="viewer-pdf-pages-panel">
          {loadError && (
            <div className="alert alert-warning alert-block" style={{ margin: 20 }}>
              <h4>Preview unavailable</h4>
              <p>A PDF could not be rendered for this document. If it has processing exceptions, see the Exceptions view.</p>
            </div>
          )}
          <div ref={containerRef} />
        </div>
      </div>
    </div>
  );
}
